import { useState } from "react";
import { Check, X, Sparkles } from "lucide-react";
import { T, withAlpha } from "../tokens";
import { Btn } from "../components/Btn";
import { usePlans, useCredits, useBuyCredits, type Plan } from "../hooks/usePayments";

const rupees = (paise: number) => `₹${Math.round(paise / 100).toLocaleString("en-IN")}`;
const perUnit = (p: Plan) => `${rupees(Math.round(p.amount / p.credits))} each`;

const WHAT_YOU_GET = [
  "Skill-by-skill match — what you've got, what's missing",
  "Title, seniority, domain & company fit, scored one by one",
  "Red flags and an apply / review / pass call",
];

function PlanCard({ plan, best, busy, onBuy }: { plan: Plan; best: boolean; busy: boolean; onBuy: () => void }) {
  const [h, setH] = useState(false);
  return (
    <div
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        position: "relative", flex: "1 1 150px", minWidth: 0,
        background: best ? T.tealBg : T.s2,
        border: `1px solid ${best ? withAlpha(T.teal, 0.4) : h ? T.b2 : T.b1}`,
        borderRadius: 12, padding: "18px 16px", transition: "border-color 0.12s ease",
      }}
    >
      {best && (
        <div style={{ position: "absolute", top: -9, left: "50%", transform: "translateX(-50%)",
          background: T.teal, color: "#04120E", fontFamily: T.mono, fontSize: 9, fontWeight: 700,
          letterSpacing: "0.06em", textTransform: "uppercase", padding: "2px 8px", borderRadius: 99 }}>
          Best value
        </div>
      )}
      <div style={{ fontFamily: T.display, fontSize: 26, fontWeight: 700, color: T.t0 }}>
        {plan.credits}
      </div>
      <div style={{ fontFamily: T.body, fontSize: 12, color: T.t2, marginBottom: 12 }}>
        {plan.credits === 1 ? "deep analysis" : "deep analyses"}
      </div>
      <div style={{ fontFamily: T.mono, fontSize: 20, fontWeight: 700, color: best ? T.tealSoft : T.t0 }}>
        {rupees(plan.amount)}
      </div>
      <div style={{ fontFamily: T.mono, fontSize: 10, color: T.t3, marginBottom: 14 }}>
        {plan.credits > 1 ? perUnit(plan) : "one-time"}
      </div>
      <Btn variant={best ? "teal" : "outline"} size="sm" onClick={onBuy} disabled={busy} style={{ width: "100%", justifyContent: "center" }}>
        {busy ? "Opening…" : "Buy"}
      </Btn>
    </div>
  );
}

export function PricingModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const plansQ  = usePlans();
  const creditsQ = useCredits();
  const buy     = useBuyCredits();
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const plans   = plansQ.data?.plans ?? [];
  const enabled = plansQ.data?.enabled ?? false;
  const bestId  = plans.reduce<Plan | null>((b, p) => (!b || p.credits > b.credits ? p : b), null)?.id;

  function purchase(planId: string) {
    setError(null);
    buy.mutate(planId, {
      onSuccess: () => onClose(),
      onError: (e) => {
        const msg = (e as { message?: string })?.message;
        if (msg !== "cancelled") setError("Payment didn't complete. Please try again.");
      },
    });
  }

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: "100%", maxWidth: 560, background: T.s1, border: `1px solid ${T.b1}`,
          borderRadius: 16, padding: "24px clamp(18px, 4vw, 28px)", position: "relative", overflow: "hidden" }}
      >
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1,
          background: `linear-gradient(90deg, transparent, ${withAlpha(T.teal, 0.5)}, transparent)` }}/>
        <button onClick={onClose} aria-label="Close" style={{ position: "absolute", top: 14, right: 14,
          background: "none", border: "none", color: T.t2, cursor: "pointer" }}>
          <X size={18}/>
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <Sparkles size={16} color={T.tealSoft}/>
          <h2 style={{ fontFamily: T.display, fontSize: 19, fontWeight: 700, color: T.t0, margin: 0 }}>
            Unlock deep analysis
          </h2>
        </div>
        <p style={{ fontFamily: T.body, fontSize: 13, color: T.t2, margin: "0 0 16px", lineHeight: 1.55 }}>
          A full AI breakdown of how you fit each role — the same depth recruiters screen for.
          {typeof creditsQ.data?.credits === "number" && creditsQ.data.credits > 0 &&
            <> You have <strong style={{ color: T.tealSoft }}>{creditsQ.data.credits}</strong> credit{creditsQ.data.credits === 1 ? "" : "s"} left.</>}
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
          {WHAT_YOU_GET.map((t) => (
            <div key={t} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
              <Check size={14} color={T.ok} style={{ marginTop: 2, flexShrink: 0 }}/>
              <span style={{ fontFamily: T.body, fontSize: 12.5, color: T.t1, lineHeight: 1.5 }}>{t}</span>
            </div>
          ))}
        </div>

        {enabled ? (
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 6 }}>
            {plans.map((p) => (
              <PlanCard key={p.id} plan={p} best={p.id === bestId} busy={buy.isPending} onBuy={() => purchase(p.id)} />
            ))}
          </div>
        ) : (
          <div style={{ background: T.s2, border: `1px solid ${T.b1}`, borderRadius: 10, padding: 16, textAlign: "center" }}>
            <p style={{ fontFamily: T.body, fontSize: 12.5, color: T.t2, margin: 0 }}>
              Payments aren't enabled yet. Please check back shortly.
            </p>
          </div>
        )}

        {error && (
          <p style={{ fontFamily: T.body, fontSize: 12, color: T.err, margin: "14px 0 0", textAlign: "center" }}>{error}</p>
        )}
        <p style={{ fontFamily: T.mono, fontSize: 9.5, color: T.t3, margin: "16px 0 0", textAlign: "center", lineHeight: 1.6 }}>
          Secure payment via Razorpay · credits never expire · Caliber never applies on your behalf
        </p>
      </div>
    </div>
  );
}
